/**
 * Tabs primitive.
 *
 * Tabbed navigation wrapped over `@radix-ui/react-tabs`. Radix owns the
 * headless behaviour — ArrowLeft / ArrowRight / Home / End keyboard
 * navigation through the trigger set, roving `tabIndex` focus
 * management, `role="tablist"` / `role="tab"` / `role="tabpanel"`
 * association, and controlled/uncontrolled state via `value` /
 * `defaultValue` / `onValueChange`. Our job is to pin token-backed
 * styling onto Radix's primitives without adding our own behaviour on
 * top.
 *
 * Compound-component shape mirrors `Dialog.tsx` and `Sheet.tsx` exactly
 * (T006 established the convention; T007 inherits): each subcomponent
 * is a **dedicated named export** (`Root`, `List`, `Trigger`, `Content`)
 * AND the module exposes an aggregate `Tabs` object via
 * `export const Tabs = { Root, List, Trigger, Content }`. Callers prefer
 * the aggregate (`<Tabs.Root><Tabs.List>…</Tabs.List></Tabs.Root>`) for
 * readability; the dedicated named exports exist so bundlers can
 * tree-shake at the subcomponent level and tests can import narrowly.
 *
 * Two visual variants are supported via a `variant` prop on `Root`:
 *
 * - `"underline"` (default) — Cluster-1 vintage list with a 1px bottom
 *   border on the list and a 2px active underline under the active
 *   trigger. Sized to content (`inline-flex`); appropriate when tabs
 *   sit above a long page and act more like section headers than a
 *   segmented control.
 * - `"pill"` — segmented-pill toggle. The list is a full-width flex
 *   container with a muted track (`bg-tab-bg`) and a small inset; each
 *   trigger uses `flex-1` so the available width is split equally
 *   regardless of how many triggers the consumer renders. Active state
 *   flips the trigger background to `bg-tab-active` with white
 *   foreground (`text-tab-active-foreground`). Use this variant for the
 *   in-page toggles consumers were previously hand-rolling
 *   per-call-site (e.g. Home's "Lakikaveri / Jäsenyys ja edut"). (Messages's
 *   former "Saapuneet / Lähetetyt" pair was RETIRED by D-220/050 — Viestit is a
 *   single list now — so it is no longer a consumer.)
 * - `"folder"` — proto folder/file tabs (024-cms Phase 19 first-screen
 *   parity). The list is a full-width WRAPPING strip with a 1px bottom
 *   border (`flex w-full flex-wrap … border-b border-border`); each
 *   trigger is a fixed 144px (`w-36`) top-rounded, open-bottom tab
 *   (`rounded-t-lg rounded-b-none border border-border border-b-0`) on a
 *   muted fill that lifts to the card surface when active
 *   (`bg-muted/60 → data-[state=active]:bg-card`), pulled down 1px
 *   (`-mb-px`) so the active tab visually merges into the panel below.
 *   This is the proto's `/hallinta` editor tab strip (D-124(iii)'s
 *   "underline" label was a misread). Use for tabs that open onto a
 *   panel surface, not the section-header `underline` posture.
 *
 * The variant propagates from `Root` through React context so `List`
 * and `Trigger` resolve the correct class strings without consumers
 * having to thread a prop into every subcomponent. Both variants share
 * the same focus ring (FR-033 sanctioned `ring-2 ring-offset-2
 * ring-ring`) and disabled treatment.
 *
 * Styling is token-backed only. Every class below is a token-backed
 * Tailwind utility — no arbitrary-bracket classes (FR-047), no raw
 * colour literals (FR-002).
 *
 * Orientation defaults to `horizontal` (Radix's default). Callers that
 * need vertical tabs pass `orientation="vertical"` through to `Root`;
 * the underline treatment reads slightly odd on a vertical list but
 * does not break — a future task that needs vertical tabs can branch
 * the class lookups on the Radix `data-orientation` attribute.
 */

import * as TabsPrimitive from '@radix-ui/react-tabs';
import { type ComponentProps, createContext, useContext } from 'react';

import { cn } from './utils.js';

export type TabsVariant = 'underline' | 'pill' | 'pill-compact' | 'folder';

export type TabsRootProps = ComponentProps<typeof TabsPrimitive.Root> & {
  readonly variant?: TabsVariant;
};

export type TabsListProps = ComponentProps<typeof TabsPrimitive.List>;
export type TabsTriggerProps = ComponentProps<typeof TabsPrimitive.Trigger>;
export type TabsContentProps = ComponentProps<typeof TabsPrimitive.Content>;

const TabsVariantContext = createContext<TabsVariant>('underline');

// Native buttons in Tailwind v4 no longer pick up `cursor-pointer` from
// preflight, so it's set explicitly here. `transition` (vs the previous
// `transition-colors`) covers transform too — `active:scale-95` gives
// the press-down feedback the rest of the shell controls share.
const sharedTriggerClasses =
  'inline-flex items-center justify-center min-h-11 px-4 py-2 text-base font-medium ' +
  'text-muted-foreground cursor-pointer hover:text-foreground ' +
  'transition active:scale-95 ' +
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-ring ' +
  'disabled:opacity-50 disabled:cursor-not-allowed';

const underlineListClasses = 'inline-flex items-center gap-1 border-b border-border';

const underlineTriggerClasses =
  `${sharedTriggerClasses} ` +
  'border-b-2 border-transparent ' +
  'data-[state=active]:text-foreground data-[state=active]:border-primary';

const pillListClasses = 'flex w-full items-center gap-1 rounded-lg bg-tab-bg p-1';

const pillTriggerClasses =
  `${sharedTriggerClasses} ` +
  'flex-1 rounded-md ' +
  'data-[state=active]:bg-tab-active data-[state=active]:text-tab-active-foreground';

// Compact pill (024-cms create kortti/nappi selector — the proto's
// CreateModeHeader). The shared `pill` above is FULL-WIDTH + min-h-11 (~44px) +
// text-base — the "massive full-width" the owner flagged. This compact variant
// matches the proto's deterministic render: a `max-w-xs` (320px) CENTERED
// (`mx-auto`), 40px-tall (`h-10`) `rounded-full` track of two equal columns
// (`grid-cols-2`), each trigger filling ~32px (`h-full`) at `text-sm` (14px).
// Distinct from `pill` so the shared Home/Messages pills are untouched. Sized by
// THIS variant, never by a conflicting className (cn() is plain concat). Token
// utilities only (FR-047): `h-10`/`max-w-xs`/`text-sm` are stock-scale in omaplus
// (NOT among the retuned `--spacing-*` overrides), so they render at 40/320/14px
// — the proto's measured numbers. Trigger base is bespoke (NOT `sharedTriggerClasses`,
// which carries the tall min-h-11/text-base).
const pillCompactListClasses =
  'mx-auto grid h-10 w-full max-w-xs grid-cols-2 items-center gap-1 rounded-full bg-tab-bg p-1';

// Active state matches the proto's CreateModeHeader trigger: brand-green track
// (NOT the proto's turquoise — D-PD-005 keeps the unified brand-green) + white
// text + font-semibold (600) + a subtle shadow-md. Idle is muted, font-medium
// (500). `px-4` + the grid-cols-2 list give two equal ~32px-tall pills.
const pillCompactTriggerClasses =
  'inline-flex h-full items-center justify-center rounded-full px-4 text-sm font-medium ' +
  'text-muted-foreground cursor-pointer hover:text-foreground ' +
  'transition active:scale-95 ' +
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-ring ' +
  'disabled:opacity-50 disabled:cursor-not-allowed ' +
  'data-[state=active]:bg-tab-active data-[state=active]:text-tab-active-foreground ' +
  'data-[state=active]:font-semibold data-[state=active]:shadow-md';

// Folder/file tabs (Phase 19): a full-width WRAPPING strip with a 1px
// bottom border the active tab merges into. Distinct from underline's
// content-sized `inline-flex` and pill's non-wrapping equal-split row.
const folderListClasses = 'flex w-full flex-wrap justify-start gap-1 border-b border-border';

// Each trigger is a fixed 144px (`w-36`) top-rounded, open-bottom folder
// tab, NARROWER than the proto's `w-[160px]` so all four tabs fit a SINGLE
// row at the desktop CMS viewport (160px wrapped Sivupohjat to row 2; 144px
// sits just under the proto's ~149px rendered width, so it stays single-row
// wherever the proto does; FR-047 token-backed). `relative` + `-mb-px` +
// `data-[state=active]:z-10` let the active tab overlap the list's bottom
// border so it reads as continuous with the panel surface.
const folderTriggerClasses =
  `${sharedTriggerClasses} ` +
  'relative min-w-0 flex-1 truncate -mb-px ' +
  'rounded-t-lg rounded-b-none border border-border border-b-0 bg-muted/60 ' +
  'hover:bg-muted/80 ' +
  'data-[state=active]:bg-card data-[state=active]:text-foreground data-[state=active]:z-10';

const contentClasses =
  'pt-4 text-foreground ' +
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-ring';

const listClassesByVariant: Readonly<Record<TabsVariant, string>> = {
  underline: underlineListClasses,
  pill: pillListClasses,
  'pill-compact': pillCompactListClasses,
  folder: folderListClasses,
};

const triggerClassesByVariant: Readonly<Record<TabsVariant, string>> = {
  underline: underlineTriggerClasses,
  pill: pillTriggerClasses,
  'pill-compact': pillCompactTriggerClasses,
  folder: folderTriggerClasses,
};

export function Root({ variant = 'underline', ...rest }: TabsRootProps) {
  return (
    <TabsVariantContext.Provider value={variant}>
      <TabsPrimitive.Root {...rest} />
    </TabsVariantContext.Provider>
  );
}

export function List({ className, ...rest }: TabsListProps) {
  const variant = useContext(TabsVariantContext);
  // `data-variant` exposes the resolved variant as a stable, class-independent
  // contract (a test or downstream style hook can read it without coupling to
  // the token-backed class strings, which are free to evolve).
  return (
    <TabsPrimitive.List
      data-variant={variant}
      className={cn(listClassesByVariant[variant], className)}
      {...rest}
    />
  );
}

export function Trigger({ className, ...rest }: TabsTriggerProps) {
  const variant = useContext(TabsVariantContext);
  return (
    <TabsPrimitive.Trigger className={cn(triggerClassesByVariant[variant], className)} {...rest} />
  );
}

export function Content({ className, ...rest }: TabsContentProps) {
  return <TabsPrimitive.Content className={cn(contentClasses, className)} {...rest} />;
}

export const Tabs = {
  Root,
  List,
  Trigger,
  Content,
} as const;
